
import { TimelineEntry as TimelineEntryType } from "@/types";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TimelineLine from "./TimelineLine";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import SecureEntryDrawer from "../SecureEntryDrawer";
import { useTranslation } from "react-i18next";
import { sanitizeUserInput, secureLog } from "@/utils/securityUtils";
import { generateTimelineDeepLink } from "@/utils/deepLinkUtils";

interface TimelineEntriesContainerProps {
  entries: TimelineEntryType[];
  isEven: boolean;
  type?: "specific" | "not-sure";
  timelineOwnerId?: string;
  useDeepLinks?: boolean; // New prop to control navigation behavior
}

const TimelineEntriesContainer = ({
  entries,
  isEven,
  type = "specific",
  timelineOwnerId,
  useDeepLinks = true // Default to using deep links
}: TimelineEntriesContainerProps) => {
  const navigate = useNavigate();
  const [selectedEntry, setSelectedEntry] = useState<TimelineEntryType | null>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const { t, i18n } = useTranslation(['timeline', 'common']);

  // Animation variants for the individual entries with stagger effect
  const entryVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
        ease: "easeOut"
      }
    })
  };

  // Fetch timeline owner username for deep links
  const { data: timelineOwnerData } = useQuery({
    queryKey: ['timeline-owner', timelineOwnerId],
    queryFn: async () => {
      if (!timelineOwnerId) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', timelineOwnerId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!timelineOwnerId && useDeepLinks
  });

  // Fetch creator usernames for all entries
  const { data: creators } = useQuery({
    queryKey: ['creators', entries.map(e => e.creator_id)],
    queryFn: async () => {
      if (entries.length === 0) return {};
      const creatorIds = [...new Set(entries.map(e => e.creator_id))];
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', creatorIds);
      
      if (error) throw error;

      return data.reduce((acc, profile) => ({
        ...acc,
        [profile.id]: profile.username
      }), {} as Record<string, string>);
    },
    enabled: entries.length > 0
  });

  const handleEntryClick = (entry: TimelineEntryType) => {
    if (useDeepLinks && timelineOwnerData?.username) {
      // Navigate using deep link with URL parameters
      const deepLink = generateTimelineDeepLink({
        username: timelineOwnerData.username,
        entryId: entry.id,
        sectionId: entry.profile_timeline_section_id
      });
      const url = new URL(deepLink);
      navigate(url.pathname + url.search);
    } else {
      // Fallback to drawer behavior
      setSelectedEntry(entry);
      setShowDrawer(true);
    }
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setSelectedEntry(null);
  };

  if (entries.length === 0) {
    return (
      <div className="text-center py-12 bg-opacity-30 relative bg-black/[0.04]">
        {/* Top shadow */}
        <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-black to-transparent opacity-5 pointer-events-none"></div>
        {/* Bottom shadow */}
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-black to-transparent opacity-5 pointer-events-none"></div>
        
        <p className="text-uplivion-blue mb-4">{t('timeline:noEvents', 'Keine Ereignisse vorhanden')}</p>
        <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-full bg-uplivion-orange hover:bg-opacity-90">
          <Plus className="mr-2 h-4 w-4" />
          {t('common:buttons.addMemory', 'Ereignis hinzufügen')}
        </button>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial="hidden"
        animate="visible"
        exit="hidden"
        className="relative w-full entries-container bg-white/[0.2]"
      >
        {/* Top shadow */}
        <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-black to-transparent opacity-5 pointer-events-none z-10"></div>
        {/* Bottom shadow */}
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-black to-transparent opacity-5 pointer-events-none z-10"></div>
        
        {/* Dotted timeline line */}
        <TimelineLine variant="dotted" />
        
        <div className="flex flex-col w-full max-w-4xl py-8 mx-auto">
          <div className="flex flex-col w-full">
            {entries.map((entry, i) => (
              <motion.div
                key={entry.id}
                className={`flex w-full mb-8 cursor-pointer ${i % 2 === 0 ? '' : 'flex-row-reverse'}`}
                custom={i}
                variants={entryVariants}
                onClick={() => handleEntryClick(entry)}
              >
                <div className={`w-1/2 ${i % 2 === 0 ? 'pr-8 items-end text-right' : 'pl-8 items-start text-left'} flex flex-col`}>               
                  {/* Photos - max 3, round */}
                  {entry.photos && entry.photos.length > 0 && (
                    <div className={`flex gap-2 mb-3 ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                      {entry.photos.slice(0, 3).map((photo, photoIndex) => (
                        <div key={photoIndex} className="w-14 h-14 rounded-full overflow-hidden bg-gray-100 border-2 border-white shadow-sm">
                          <img src={photo} alt={`Entry image ${photoIndex + 1}`} className="object-cover w-full h-full" />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Date in orange - handle different display for not-sure entries */}
                  <h3 className="text-lg font-bold leading-tight text-uplivion-orange mb-2">
                    {type === "not-sure" || (!entry.year && !entry.month) ?
                      // For entries without any date information, show "Unknown Date"
                      t('timeline:unknownDate', 'Unbekanntes Datum') :
                      entry.day ?
                        // For entries with full date - show in format "08.April.2001"
                        new Date(entry.date).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'de-DE', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        }) :
                        entry.month && entry.year ?
                          // For entries with month and year
                          new Date(entry.year, entry.month - 1).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'de-DE', {
                            month: 'long',
                            year: 'numeric'
                          }) :
                          entry.year ?
                            // For entries with only year
                            entry.year.toString() :
                            // Fallback to month/year from date field
                            new Date(entry.date).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'de-DE', {
                              month: 'long',
                              year: 'numeric'
                            })
                    }
                  </h3>                
                  
                  {/* Title in blue - sanitized */}
                  <p className="text-sm font-semibold leading-tight text-uplivion-blue mb-1">
                    {sanitizeUserInput(entry.title)}
                  </p>
                  
                  {/* Creator info - show username */}
                  {creators && creators[entry.creator_id] && (
                    <p className="text-xs text-uplivion-blue text-opacity-60">
                      {sanitizeUserInput(creators[entry.creator_id])}
                    </p>
                  )}
                </div>
                <div className="w-1/2"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {selectedEntry && (
        <SecureEntryDrawer 
          entry={selectedEntry} 
          isOpen={showDrawer} 
          onClose={handleCloseDrawer}
          timelineOwnerId={timelineOwnerId}
        />
      )}
    </>
  );
};

export default TimelineEntriesContainer;
